import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import Table from "../../../components/Table/Table";
import { setParameterCategoryEnumExtraProperties } from "../../../utils/pdApiUtils";
import { EditActions, editActions } from "../../../actions/editActions";
import { PdApiActions, pdApiActions } from "../../../actions/pdApiActions";
import { editedObjectIdSelector, isEditActiveSelector } from "../../../reducers/editReducer";
import _ from "lodash";
import EditParameterCategory from "../../Editing/EditParameterCategory";
import { Dimmer } from "semantic-ui-react";
import { mainActions, MainActions } from "../../../actions/mainActions";
import { Filter } from "../../../components/Table/Migration/LegacyTypes";

type StateProps = {
  pdApi: State.PdApi;
  columns: State.ColumnMap;
  mainState: State.Main;
  editedObjectId: number | undefined;
  editedObject?: Api.AnyItem;
  isEditActive: boolean;
  mainActions: typeof mainActions;
  subMenuRoute: State.LiftSubmenu;
  columnFilters: Filter[];
  setColumnFilters: (subMenuRoute: State.LiftSubmenu, columnFilter: Filter[]) => void;
};

type DispatchProps = {
  editActions: typeof editActions;
  pdApiActions: typeof pdApiActions;
};

type OwnProps = {
  subMenuRoute: string;
  groupName?: Api.GroupName;
  toggleModal: () => void;
};

// Props of the component
export type ParameterCategoriesTableProps = StateProps & DispatchProps & OwnProps;

type ParameterCategoriesTableState = {
  isModalOpen: boolean;
  selectedParentId?: number;
};

export class ParameterCategoriesTable extends React.Component<
  ParameterCategoriesTableProps,
  ParameterCategoriesTableState
> {
  state = {
    isModalOpen: false,
    selectedParentId: undefined
  };

  componentDidMount(): void {
    // make throttle function
    this.handleFilterChangeDebounced = _.debounce(this.handleFilterChangeDebounced, 500);

    if (this.props.pdApi.driveParameters === undefined && this.props.groupName === "Drive") {
      this.props.pdApiActions.fetchDriveParameters();
    }
  }

  private handleFilterChangeDebounced = (changedFilters: Filter[]) => {
    this.props.setColumnFilters(this.props.subMenuRoute, changedFilters);
  };

  componentWillUnmount(): void {
    this.props.mainActions.setLastEditedObjectId(undefined);
  }
  render(): JSX.Element {
    const { columns, pdApi, mainState, isEditActive, groupName, toggleModal } = this.props;
    const { parameterCategories } = pdApi;
    const parameterCategoriesAsArray = _.values(parameterCategories);
    const { editSize, pageSize, isShortenTextEnabled } = mainState;
    let rows: Api.ParameterCategory[] = [...parameterCategoriesAsArray]
      .sort(function(a: Api.ParameterCategory, b: Api.ParameterCategory) {
        return a.enumValue - b.enumValue;
      })
      .map(row => setParameterCategoryEnumExtraProperties(row as Api.ParameterCategory, pdApi));
    // filter results by DomainGroup if provided and remove column
    let selectedColumns: State.Column[] = columns["Parameter Categories"];
    const domainGroupId: number | undefined = _.values(pdApi.domainGroups).find(
      (domainGroup: Api.DomainGroup) => domainGroup.enumName === groupName
    )?.id;
    if (domainGroupId) {
      rows = rows.filter(row => row.domainGroup.id === domainGroupId);
      selectedColumns = selectedColumns.filter(row => row.name !== "Domain Group");
    }

    // Split parameters into parent and sub cageories, display parents left
    const parentRows = rows.filter(row => row.parentCategory === null);
    let subRows = null;
    if (this.state.selectedParentId) {
      subRows = rows.filter(
        row => row.parentCategory && row.parentCategory.id === this.state.selectedParentId
      );
      subRows.unshift(rows.filter(row => row.id === this.state.selectedParentId)[0]); // push root on top
    }
    const startingPage = mainState.lastEditedObjectId
      ? (parentRows.findIndex(
          parentParameterCategory => parentParameterCategory.id === mainState.lastEditedObjectId
        ) / // tslint:disable-line: no-bitwise
          mainState.pageSize) >>
        0
      : 0;
    return (
      <div className="flex-1">
        <div className={`flex-1 ${subRows ? "parameterTable" : ""}`}>
          <Table
            startingPage={startingPage}
            rows={parentRows}
            columns={selectedColumns}
            isHidingTextActive={isShortenTextEnabled}
            // Disable rowSelection of root parameter categories when editing to prevent
            // sync issues between edited info and ui
            handleRowSelection={
              isEditActive
                ? () => {
                    /*  */
                  }
                : this.handleParentParameterCategorySelection
            }
            isRowSelected={this.isParentParameterCategorySelected}
            pageSize={pageSize}
            handleFilterChange={this.handleFilterChangeDebounced}
          />
          {subRows && (
            <Table
              rows={subRows}
              columns={selectedColumns}
              isHidingTextActive={isShortenTextEnabled}
              handleRowSelection={this.props.editActions.select}
              isRowSelected={this.isParameterCategorySelected}
              pageSize={pageSize}
              disablePagination={false}
            />
          )}
        </div>
        {isEditActive && (
          <Dimmer active={true} page={true}>
            <div
              className="edit-view"
              style={{ flex: editSize, width: editSize == 0.3 ? "500px" : "1000px" }}
            >
              <EditParameterCategory
                selectedParentId={this.state.selectedParentId}
                domainGroupId={domainGroupId}
                toggleModal={toggleModal}
              />
            </div>
          </Dimmer>
        )}
      </div>
    );
  }

  /*
   * Returns status if parameterCategory has been selected
   * @param {Api.AnyEnumeration} parameter
   * @returns {boolean}
   */
  private isParameterCategorySelected = (parameterCategory: Api.ParameterCategory): boolean => {
    const { editedObject, editedObjectId } = this.props;
    const selectedId = editedObject && editedObject.id === undefined ? undefined : editedObjectId;
    if (selectedId !== undefined && parameterCategory.id === selectedId) {
      return true;
    } else {
      return false;
    }
  };

  private handleParentParameterCategorySelection = (
    selectedParameterCategory: Api.ParameterCategory
  ): void => {
    this.setState(prevState => ({
      selectedParentId:
        selectedParameterCategory.id === prevState.selectedParentId
          ? undefined
          : selectedParameterCategory.id
    }));
  };
  private isParentParameterCategorySelected = (parameterCategory: Api.AnyItem): boolean => {
    const editedObjectId =
      this.props.editedObject && this.props.editedObject.id === undefined
        ? undefined
        : this.props.editedObjectId ?? this.props.mainState.lastEditedObjectId;
    if (
      parameterCategory?.id === this.state.selectedParentId ||
      editedObjectId === parameterCategory.id
    ) {
      return true;
    } else {
      return false;
    }
  };
}

const mapStateToProps = (state: State.Root) => ({
  columns: state.columns,
  mainState: state.main,
  editedObjectId: editedObjectIdSelector(state.editView),
  editedObject: state.editView.editedObject,
  isEditActive: isEditActiveSelector(state.editView)
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions | MainActions | PdApiActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch),
  mainActions: bindActionCreators({ ...mainActions }, dispatch),
  pdApiActions: bindActionCreators({ ...pdApiActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(ParameterCategoriesTable);

